import React from 'react';
import { useSubscription } from '@/hooks/use-subscription';
import { supabase } from '@/integrations/supabase/client';

/**
 * Simple dev controls for testing monetization features
 * Only visible in development mode
 */
export const DevControls = () => {
  // Get subscription context functions - must call hooks at the top level
  const { openPricingModal, trackPaywallHit } = useSubscription();
  
  // Only show in development mode
  if (process.env.NODE_ENV !== 'development') return null;
  
  // Set user as in trial with 1 day left
  const setTrial = () => {
    localStorage.setItem('strykr_subscription_data', JSON.stringify({
      subscription_status: 'trialing',
      trial_end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    }));
    // Also set usage data
    localStorage.setItem('strykr_usage_data', JSON.stringify({
      queryCount: 50,
      queryLimit: 250, // Higher limit for trial users
      calendarDaysLimit: Infinity
    }));
    window.location.reload();
  };
  
  // Set user as expired/free
  const setFree = () => {
    localStorage.setItem('strykr_subscription_data', JSON.stringify({
      subscription_status: 'expired',
      trial_end_date: null
    }));
    // Set limited usage for free users
    localStorage.setItem('strykr_usage_data', JSON.stringify({
      queryCount: 10,
      queryLimit: 25,
      calendarDaysLimit: 2
    }));
    window.location.reload();
  };
  
  // Set user as subscribed
  const setSubscribed = () => {
    localStorage.setItem('strykr_subscription_data', JSON.stringify({
      subscription_status: 'active',
      trial_end_date: null
    }));
    // Unlimited usage for paid users
    localStorage.setItem('strykr_usage_data', JSON.stringify({
      queryCount: 0,
      queryLimit: Infinity,
      calendarDaysLimit: Infinity
    }));
    window.location.reload();
  };
  
  // Set query limit close to max and show limit alert
  const setNearLimit = () => {
    // Update localStorage values directly - this is most reliable
    localStorage.setItem('strykr_subscription_data', JSON.stringify({
      subscription_status: null,
      trial_end_date: null,
      timestamp: new Date().toISOString()
    }));
    
    // Set almost at query limit in localStorage
    localStorage.setItem('strykr_usage_data', JSON.stringify({
      queryCount: 4,
      queryLimit: 5,
      lastResetDate: new Date().toDateString()
    }));

    // Set specific query limit data for the hook
    localStorage.setItem('daily_query_limit', JSON.stringify({
      count: 4,
      max: 5,
      date: new Date().toDateString()
    }));
    
    // We need to force a page reload to apply these changes
    window.location.reload();
  };
  
  // Create a paywall hit and trigger the modal directly
  const triggerPaywall = () => {
    // Record a paywall hit
    const now = Date.now();
    localStorage.setItem('strykr_paywall_session', JSON.stringify({
      hits: [
        { feature: 'calendar', timestamp: now - 300000 },
        { feature: 'market_data', timestamp: now }
      ],
      timestamp: now,
      lastModalShown: now - 7200000 // 2 hours ago (will trigger modal)
    }));
    
    // Also set as free user
    localStorage.setItem('strykr_subscription_data', JSON.stringify({
      subscription_status: 'expired',
      trial_end_date: null
    }));
    
    // Track a paywall hit and directly open the pricing modal
    trackPaywallHit('dev_controls_trigger');
    openPricingModal('dev_controls_trigger');
  };

  return (
    <div className="fixed bottom-3 right-3 z-50 bg-black/80 text-white p-2 rounded shadow-lg text-xs">
      <div className="font-bold mb-1">Dev Controls</div>
      <div className="flex flex-col gap-1">
        <button onClick={setTrial} className="bg-blue-600 px-2 py-1 rounded hover:bg-blue-700 text-left">
          🔹 Trial (1 day left)
        </button>
        <button onClick={setFree} className="bg-red-600 px-2 py-1 rounded hover:bg-red-700 text-left">
          🔹 Free User
        </button>
        <button onClick={setSubscribed} className="bg-green-600 px-2 py-1 rounded hover:bg-green-700 text-left">
          🔹 Subscribed
        </button>
        <button onClick={setNearLimit} className="bg-yellow-600 px-2 py-1 rounded hover:bg-yellow-700 text-left">
          🔹 Near Query Limit
        </button>
        <button onClick={triggerPaywall} className="bg-purple-600 px-2 py-1 rounded hover:bg-purple-700 text-left">
          🔹 Trigger Paywall
        </button>
      </div>
    </div>
  );
};
